// v3
// Path: frontend/src/components/SyncHub/shared/MappingDesignerTable.jsx
// Inputs: isStandardMode, selectedQBEntity, qbSampleRecords, activeQbSampleIndex, setActiveQbSampleIndex, pbsSampleRecords, activeSampleIndex, setActiveSampleIndex, activeTableName, mappings, definitions, sourceColumns, newTarget, setNewTarget, newSource, setNewSource, handleAddCustomMapping, handleCustomMappingChange, handleRemoveCustomRow
// Outputs: Strict pixel-aligned 3-column CSS Grid ETL mapping table (Source Builder -> Transform & Operator -> Target Destination) with active powered glow states, 3-column field filter bar, and custom target endpoint creation.
// Summary: High-performance 3-column Left-to-Right ETL mapping canvas enforcing unified pixel alignment, powered visual glow for active mappings, dimmed unmapped rows, and multi-source token concatenation.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import SearchableSelect from '../SearchableSelect';

export default function MappingDesignerTable(props) {
    const {
        isStandardMode = false,
        selectedQBEntity = 'Customer',
        qbSampleRecords = [],
        activeQbSampleIndex = 0,
        setActiveQbSampleIndex = () => {},
        pbsSampleRecords = [],
        activeSampleIndex = 0,
        setActiveSampleIndex = () => {},
        activeTableName = '',
        mappings = [],
        definitions = null,
        sourceColumns = [],
        newTarget = '',
        setNewTarget = () => {},
        newSource = '',
        setNewSource = () => {},
        handleAddCustomMapping = () => {},
        handleCustomMappingChange = () => {},
        handleRemoveCustomRow = () => {}
    } = props;

    // Field Filter Bar State (3-column split: Source Filter | [All | Mapped | Unmapped] | Target Filter)
    const [sourceFilter, setSourceFilter] = useState('');
    const [mappingFilter, setMappingFilter] = useState('all'); // 'all' | 'mapped' | 'unmapped'
    const [targetFilter, setTargetFilter] = useState('');

    // State for "+ Add Field" popover
    const [activePopoverTarget, setActivePopoverTarget] = useState(null);
    const [popoverSearch, setPopoverSearch] = useState('');
    const popoverRef = useRef(null);

    // Light dismiss for column popover (click outside or Esc)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };

        if (activePopoverTarget) {
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePopoverTarget]);

    // Normalize source columns from definitions or props
    const rawCols = definitions?.sourceFields || definitions?.sourceColumns || sourceColumns || [];
    const sourceColumnsList = useMemo(() => {
        return rawCols.map(col => {
            const colName = typeof col === 'string' ? col : (col.name || col.COLUMN_NAME || '');
            const desc = typeof col === 'object' ? (col.description || col.label || '') : '';
            const colType = typeof col === 'object' ? (col.type || 'string') : 'string';
            const isPk = typeof col === 'object' ? (col.isPrimaryKey || false) : false;
            const colLabel = desc && desc !== colName ? `${colName} (${desc})` : colName;
            return { name: colName, label: colLabel, description: desc, type: colType, isPrimaryKey: isPk };
        }).filter(c => c.name);
    }, [rawCols]);

    // Target fields lookup for types and requirements
    const targetFieldsLookup = useMemo(() => {
        const map = new Map();
        (definitions?.targetFields || []).forEach(tf => {
            map.set(tf.name.toLowerCase(), tf);
        });
        return map;
    }, [definitions]);

    // Helper to extract live QB sample values
    const getQbSampleValue = (fieldPath) => {
        if (!qbSampleRecords || qbSampleRecords.length === 0) return null;
        const rec = qbSampleRecords[activeQbSampleIndex];
        if (!rec) return null;
        const parts = fieldPath.split('.');
        let val = rec;
        for (const p of parts) {
            if (val == null) break;
            val = val[p];
        }
        return val;
    };

    // Helper to extract live PBS sample values
    const getPbsSampleValue = (fieldNames, template, delimiter) => {
        if (!pbsSampleRecords || pbsSampleRecords.length === 0) return null;
        const rec = pbsSampleRecords[activeSampleIndex];
        if (!rec) return null;
        if (template) {
            let str = template;
            fieldNames.forEach(f => {
                const regex = new RegExp(`\\{${f}\\}`, 'g');
                str = str.replace(regex, rec[f] !== undefined ? String(rec[f]).trim() : '');
            });
            return str;
        }
        const delim = delimiter !== undefined ? delimiter : ' - ';
        const vals = fieldNames
            .map(f => (rec[f] !== undefined && rec[f] !== null) ? String(rec[f]).trim() : '')
            .filter(v => v.length > 0);
        return vals.join(delim);
    };

    // Filter available columns for the active popover
    const filteredPopoverColumns = useMemo(() => {
        if (!popoverSearch.trim()) return sourceColumnsList;
        const q = popoverSearch.toLowerCase().trim();
        return sourceColumnsList.filter(col =>
            col.name.toLowerCase().includes(q) ||
            col.label.toLowerCase().includes(q) ||
            col.description.toLowerCase().includes(q)
        );
    }, [sourceColumnsList, popoverSearch]);

    // Filter rows based on Field Filter Bar
    const visibleMappings = useMemo(() => {
        return mappings.filter(m => {
            const assignedSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            const hasSources = assignedSources.length > 0 && assignedSources.some(s => s && s.trim().length > 0);

            // Filter by mapped/unmapped pill
            if (mappingFilter === 'mapped' && !hasSources) return false;
            if (mappingFilter === 'unmapped' && hasSources) return false;

            // Filter by left source search term
            if (sourceFilter.trim()) {
                const sq = sourceFilter.toLowerCase().trim();
                const matchesSource = assignedSources.some(s => s && s.toLowerCase().includes(sq));
                if (!matchesSource) return false;
            }

            // Filter by right target search term
            if (targetFilter.trim()) {
                const tq = targetFilter.toLowerCase().trim();
                const targetMeta = targetFieldsLookup.get((m.target || '').toLowerCase());
                const matchesTarget = (m.target || '').toLowerCase().includes(tq) ||
                    (targetMeta?.description && targetMeta.description.toLowerCase().includes(tq));
                if (!matchesTarget) return false;
            }

            return true;
        });
    }, [mappings, mappingFilter, sourceFilter, targetFilter, targetFieldsLookup]);

    const mappedCount = useMemo(() => {
        return mappings.filter(m => {
            const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            return s.length > 0 && s.some(x => x && x.trim().length > 0);
        }).length;
    }, [mappings]);

    return (
        <div className="flex flex-col h-full bg-slate-900 overflow-hidden text-xs">
            {/* Live Sample Navigators - Shown in Pipeline Mode */}
            {!isStandardMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-950/70 border-b border-slate-800 shrink-0 text-xs">
                    {/* LEFT: Source ERP Sample Navigator */}
                    <div className="bg-slate-900 border border-emerald-900/50 rounded-xl p-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-400">
                            <span className="material-symbols-outlined text-[16px]">database</span>
                            <span>Source Record ({activeTableName || 'ERP'})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(0)}
                                disabled={activeSampleIndex === 0 || !pbsSampleRecords || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&laquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.max(0, activeSampleIndex - 1))}
                                disabled={activeSampleIndex === 0 || !pbsSampleRecords || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {pbsSampleRecords?.length > 0 ? activeSampleIndex + 1 : 0} / {pbsSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.min((pbsSampleRecords?.length || 1) - 1, activeSampleIndex + 1))}
                                disabled={!pbsSampleRecords || activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex((pbsSampleRecords?.length || 1) - 1)}
                                disabled={!pbsSampleRecords || activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&raquo;</button>
                        </div>
                    </div>

                    {/* RIGHT: Target QuickBooks Sample Navigator */}
                    <div className="bg-slate-900 border border-purple-900/50 rounded-xl p-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-purple-300">
                            <span className="material-symbols-outlined text-[16px]">cloud</span>
                            <span>Target Record ({selectedQBEntity})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(0)}
                                disabled={activeQbSampleIndex === 0 || !qbSampleRecords || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&laquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.max(0, activeQbSampleIndex - 1))}
                                disabled={activeQbSampleIndex === 0 || !qbSampleRecords || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {qbSampleRecords?.length > 0 ? activeQbSampleIndex + 1 : 0} / {qbSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.min((qbSampleRecords?.length || 1) - 1, activeQbSampleIndex + 1))}
                                disabled={!qbSampleRecords || activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex((qbSampleRecords?.length || 1) - 1)}
                                disabled={!qbSampleRecords || activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&raquo;</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* FIELD FILTER BAR (Strict 3 Columns: 1fr - 200px - 1fr)                 */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            <div className="grid grid-cols-[minmax(0,1fr)_200px_minmax(0,1fr)] gap-4 items-center px-4 py-2 bg-slate-950/90 border-b border-slate-800 shrink-0 shadow-sm z-10">
                {/* Left: Filter Source Fields */}
                <div className="relative w-full">
                    <span className="material-symbols-outlined text-slate-500 absolute left-2.5 top-1.5 text-[15px]">search</span>
                    <input
                        type="text"
                        value={sourceFilter}
                        onChange={e => setSourceFilter(e.target.value)}
                        placeholder="🔍 Filter source fields..."
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-lg pl-8 pr-7 py-1 text-xs text-emerald-300 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    />
                    {sourceFilter && (
                        <button
                            type="button"
                            onClick={() => setSourceFilter('')}
                            className="absolute right-2 top-1 text-slate-400 hover:text-white text-xs font-bold"
                        >&times;</button>
                    )}
                </div>

                {/* Center: [All | Mapped | Unmapped] Segmented Toggle */}
                <div className="flex items-center justify-center p-0.5 bg-slate-900 border border-slate-700/80 rounded-lg w-full">
                    <button
                        type="button"
                        onClick={() => setMappingFilter('all')}
                        className={`flex-1 py-0.5 text-[10px] font-bold rounded transition-colors text-center ${
                            mappingFilter === 'all'
                                ? 'bg-indigo-600 text-white shadow'
                                : 'text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        All ({mappings.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setMappingFilter('mapped')}
                        className={`flex-1 py-0.5 text-[10px] font-bold rounded transition-colors text-center ${
                            mappingFilter === 'mapped'
                                ? 'bg-emerald-600 text-white shadow'
                                : 'text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        Mapped ({mappedCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setMappingFilter('unmapped')}
                        className={`flex-1 py-0.5 text-[10px] font-bold rounded transition-colors text-center ${
                            mappingFilter === 'unmapped'
                                ? 'bg-amber-600 text-white shadow'
                                : 'text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        Unmapped ({Math.max(0, mappings.length - mappedCount)})
                    </button>
                </div>

                {/* Right: Filter Targets */}
                <div className="relative w-full">
                    <span className="material-symbols-outlined text-slate-500 absolute left-2.5 top-1.5 text-[15px]">search</span>
                    <input
                        type="text"
                        value={targetFilter}
                        onChange={e => setTargetFilter(e.target.value)}
                        placeholder="🔍 Filter targets..."
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-lg pl-8 pr-7 py-1 text-xs text-purple-300 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                    {targetFilter && (
                        <button
                            type="button"
                            onClick={() => setTargetFilter('')}
                            className="absolute right-2 top-1 text-slate-400 hover:text-white text-xs font-bold"
                        >&times;</button>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* FIELD MAPPING CANVAS (Strict 3 Columns: 1fr - 200px - 1fr per row)     */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                {visibleMappings.length === 0 ? (
                    <div className="px-4 py-12 text-center text-slate-400 bg-slate-950/40 rounded-xl border border-slate-800">
                        <span className="material-symbols-outlined text-3xl mb-1 text-slate-500">search_off</span>
                        <p className="text-sm font-semibold">No field mappings match your filters</p>
                        <p className="text-xs text-slate-500 mt-1">Try resetting the filter pills or clearing the search text.</p>
                    </div>
                ) : null}

                {visibleMappings.map((mapping, idx) => {
                    const targetMeta = targetFieldsLookup.get((mapping.target || '').toLowerCase());
                    const isReq = targetMeta?.required || false;
                    const isCustom = !targetMeta;
                    const targetType = targetMeta?.type || 'string';
                    const qbVal = isStandardMode ? null : getQbSampleValue(mapping.target);
                    const pbsVal = isStandardMode ? null : getPbsSampleValue(mapping.sources || [], mapping.template, mapping.delimiter);
                    const isPopoverOpen = activePopoverTarget === mapping.target;
                    const assignedSources = Array.isArray(mapping.sources) ? mapping.sources : (mapping.source ? [mapping.source] : []);
                    const hasAssignedSources = assignedSources.length > 0 && assignedSources.some(s => s && s.trim().length > 0);

                    return (
                        <div
                            key={`${mapping.target}-${idx}`}
                            className={`grid grid-cols-[minmax(0,1fr)_200px_minmax(0,1fr)] gap-4 items-center px-4 py-2.5 rounded-xl border transition-all ${
                                hasAssignedSources
                                    ? 'border-cyan-500/50 bg-slate-850/95 shadow-[0_0_12px_rgba(6,182,212,0.12)] text-white'
                                    : 'border-slate-800/80 bg-slate-900/40 text-slate-400 opacity-60 hover:opacity-100 hover:border-slate-750'
                            }`}
                        >
                            {/* ── 1. LEFT COLUMN (1fr): SOURCE BUILDER (INPUT) ── */}
                            <div className="relative min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 min-h-[28px]">
                                    {/* Assigned Column Tokens */}
                                    {assignedSources.map((src, sIdx) => (
                                        <span
                                            key={sIdx}
                                            className="inline-flex items-center gap-1 bg-emerald-950/90 border border-emerald-600/70 text-emerald-300 px-2 py-0.5 rounded-md font-mono text-[11px] shadow-sm"
                                            title={`Assigned ERP source column #${sIdx + 1}`}
                                        >
                                            <span className="font-bold">{src}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const ns = assignedSources.filter((_, i) => i !== sIdx);
                                                    handleCustomMappingChange(mapping.target, 'sources', ns);
                                                }}
                                                className="text-emerald-400/60 hover:text-rose-400 font-bold transition-colors ml-0.5 focus:outline-none"
                                                title="Remove column from this mapping"
                                            >&times;</button>
                                        </span>
                                    ))}

                                    {/* Add Field Button & Popover Trigger */}
                                    <div className="relative inline-block">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isPopoverOpen) {
                                                    setActivePopoverTarget(null);
                                                    setPopoverSearch('');
                                                } else {
                                                    setActivePopoverTarget(mapping.target);
                                                    setPopoverSearch('');
                                                }
                                            }}
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium transition-colors ${
                                                hasAssignedSources
                                                    ? 'bg-slate-800 hover:bg-slate-750 text-slate-200 border-slate-700'
                                                    : 'bg-indigo-950/40 hover:bg-indigo-900/60 text-indigo-300 border-indigo-800/60'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[13px]">add</span>
                                            <span>{hasAssignedSources ? 'Add Field' : 'Add Source Field'}</span>
                                        </button>

                                        {/* Searchable Column Selection Popover */}
                                        {isPopoverOpen && (
                                            <div
                                                ref={popoverRef}
                                                className="absolute left-0 top-full mt-1 w-72 bg-slate-950 border border-slate-700 rounded-xl shadow-2xl p-2 z-50 space-y-1.5 animate-in fade-in zoom-in-95 duration-100"
                                            >
                                                <div className="relative">
                                                    <span className="material-symbols-outlined text-slate-400 text-[14px] absolute left-2 top-2">search</span>
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        value={popoverSearch}
                                                        onChange={e => setPopoverSearch(e.target.value)}
                                                        placeholder="Search column or label..."
                                                        className="w-full bg-slate-900 border border-slate-700 rounded-md pl-7 pr-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500 font-mono"
                                                    />
                                                </div>
                                                <div className="max-h-48 overflow-y-auto divide-y divide-slate-800/40 text-[11px]">
                                                    {filteredPopoverColumns.length === 0 ? (
                                                        <div className="p-2 text-center text-slate-500 italic">
                                                            No columns found
                                                        </div>
                                                    ) : (
                                                        filteredPopoverColumns.map(col => (
                                                            <button
                                                                key={col.name}
                                                                type="button"
                                                                onClick={() => {
                                                                    if (!assignedSources.includes(col.name)) {
                                                                        const ns = [...assignedSources, col.name];
                                                                        handleCustomMappingChange(mapping.target, 'sources', ns);
                                                                    }
                                                                    setActivePopoverTarget(null);
                                                                    setPopoverSearch('');
                                                                }}
                                                                className="w-full text-left px-2 py-1.5 hover:bg-slate-800/80 rounded flex flex-col gap-0.5 transition-colors"
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <span className="font-mono font-bold text-emerald-400">{col.name}</span>
                                                                    {col.isPrimaryKey && (
                                                                        <span className="text-[9px] px-1 py-0.2 bg-amber-950 text-amber-400 rounded border border-amber-800 font-mono">PK</span>
                                                                    )}
                                                                </div>
                                                                {col.description && (
                                                                    <span className="text-[10px] text-slate-400 truncate">{col.description}</span>
                                                                )}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Live Source Sample Snippet (Pipeline Mode) */}
                                {!isStandardMode && pbsVal && (
                                    <div className="mt-1 text-[10px] text-emerald-300 font-mono bg-slate-950/70 px-2 py-0.5 rounded border border-slate-800/80 overflow-x-auto whitespace-nowrap">
                                        <span className="text-slate-500 font-sans mr-1">ERP:</span>
                                        <span>{pbsVal}</span>
                                    </div>
                                )}
                            </div>

                            {/* ── 2. CENTER COLUMN (200px): TRANSFORMATION & OPERATOR ── */}
                            <div className="flex items-center justify-center gap-2 text-center shrink-0">
                                {assignedSources.length > 1 ? (
                                    <div className="flex items-center gap-1" title="Separator delimiter for multiple source fields">
                                        <span className="text-[10px] text-slate-400 uppercase font-bold">Delimiter:</span>
                                        <select
                                            value={mapping.delimiter !== undefined ? mapping.delimiter : ' - '}
                                            onChange={(e) => handleCustomMappingChange(mapping.target, 'delimiter', e.target.value)}
                                            className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-amber-300 font-mono text-[10px] focus:outline-none focus:border-indigo-500"
                                        >
                                            <option value=" - ">- (dash)</option>
                                            <option value=" "> (space)</option>
                                            <option value=", ">, (comma)</option>
                                            <option value="/">/ (slash)</option>
                                            <option value="">(none)</option>
                                        </select>
                                        <span className="text-indigo-400 font-bold ml-1 text-sm">➔</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 text-slate-500 font-bold text-sm">
                                        <span className="material-symbols-outlined text-[18px]">trending_flat</span>
                                    </div>
                                )}
                            </div>

                            {/* ── 3. RIGHT COLUMN (1fr): TARGET DESTINATION (ENDPOINT) ── */}
                            <div className="flex items-center justify-between gap-2 min-w-0">
                                <div className="space-y-0.5 truncate">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`font-mono text-xs font-semibold truncate ${
                                            hasAssignedSources ? 'text-white' : 'text-slate-300'
                                        }`}>
                                            {mapping.target}
                                        </span>
                                        {isReq && (
                                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-950 text-amber-300 border border-amber-800 shrink-0" title="Required Target Field">
                                                Required *
                                            </span>
                                        )}
                                        {isCustom && (
                                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-indigo-950 text-indigo-300 border border-indigo-800 shrink-0">
                                                CUSTOM
                                            </span>
                                        )}
                                        <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-slate-800 text-slate-400 shrink-0">
                                            {targetType}
                                        </span>
                                    </div>
                                    {targetMeta?.description && (
                                        <p className="text-[10px] text-slate-400 leading-tight truncate">
                                            {targetMeta.description}
                                        </p>
                                    )}
                                </div>

                                {/* Action Button: Clear or Delete Mapping */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isCustom) {
                                            handleRemoveCustomRow(mapping.target);
                                        } else {
                                            handleCustomMappingChange(mapping.target, 'sources', []);
                                        }
                                    }}
                                    className="text-slate-500 hover:text-rose-400 transition-colors p-1 rounded hover:bg-slate-800 shrink-0 focus:outline-none"
                                    title={isCustom ? "Delete custom mapping row" : "Clear mapped source fields"}
                                >
                                    <span className="material-symbols-outlined text-[16px]">
                                        {isCustom ? 'delete' : 'backspace'}
                                    </span>
                                </button>
                            </div>
                        </div>
                    );
                })}

                {/* ── ROW LAST: CUSTOM TARGET ENDPOINT CREATION ROW (STRICT 3 COLUMNS) ── */}
                <div className="grid grid-cols-[minmax(0,1fr)_200px_minmax(0,1fr)] gap-4 items-center px-4 py-2.5 rounded-xl border border-dashed border-slate-700/80 bg-slate-950/40 text-slate-300 shadow-inner">
                    {/* Left (1fr): Source Column Selector / Input */}
                    <div className="flex items-center gap-2">
                        <SearchableSelect
                            options={sourceColumnsList.map(c => ({
                                value: c.name,
                                label: c.label,
                                description: c.description
                            }))}
                            value={newSource}
                            onChange={val => setNewSource(val || '')}
                            placeholder="+ Select or enter source field..."
                            allowCustom={true}
                            className="w-full"
                        />
                    </div>

                    {/* Center (200px): Flow Link Indicator */}
                    <div className="flex items-center justify-center gap-1 text-slate-500 font-bold text-sm">
                        <span>Delimiter: [ - ]</span>
                        <span className="material-symbols-outlined text-[18px]">trending_flat</span>
                    </div>

                    {/* Right (1fr): Custom Target Name & Add Button */}
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={newTarget}
                            onChange={e => setNewTarget(e.target.value)}
                            placeholder="+ Custom target endpoint field..."
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-purple-300 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                        />
                        <button
                            type="button"
                            onClick={handleAddCustomMapping}
                            disabled={!newTarget.trim() || !newSource.trim()}
                            className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg text-xs font-semibold shrink-0 transition-colors flex items-center gap-1 shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[14px]">add</span>
                            <span>Add</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
